import { IContextualMenuItem } from "@fluentui/react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { newTemplateFilterEnum } from "../../../src/taskpane/components/enums/newTemplateFilterEnum";
import { FolderViewWrapper } from "../../../src/taskpane/components/helpers/folderViewWrapper";
import { ComponentTypes } from "../../../src/taskpane/config/config";
import { SharePointFolderContentTypeIds } from "../../../src/taskpane/config/constants";
import { RootState } from "../../../src/taskpane/redux";
import { setIsBusySaving, setIsSavingInBackground } from "../../../src/taskpane/redux/modules/savingInBackground";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { NewDocumentTemplate } from "../../../src/taskpane/interfaces/IDocumentTemplate";

const rootContentType: string = "root";

export function useMenuItem_Upload(
    selectedItems: any[],
    waitFor: boolean,
    newDocumentTemplates: NewDocumentTemplate[],
    pickFiles: () => void,
    pickFolder: () => void,
    setIsCMUploadFilesPanelOpen: (isCMUploadFilesPanelOpen: boolean) => void
) {
    const languageStrings = React.useContext(LanguageContext);

    const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);
    const currentDirectory = useSelector((state: RootState) =>
        state.workingDirectories.workingDirectories.length > 0
            ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1]
            : null
    );
    const isBusySaving = useSelector((state: RootState) => state.savingInBackground.isBusySaving);
    const siteInfo = useSelector((state: RootState) => state.tenantInfo.sites);
    const searchValue = useSelector((state: RootState) => state.contentManagerDirectory?.searchValue?.value);
    const enableNewTemplateFilters = useSelector((state: RootState) => state.tenantInfo.enableNewTemplateFilters);
    const newTemplateDocumentSetFilters = useSelector((state: RootState) => state.tenantInfo.newTemplateDocumentSetFilters);
    const newTemplateFolderFilters = useSelector((state: RootState) => state.tenantInfo.newTemplateFolderFilters);
    const newTemplateLibraryFilters = useSelector((state: RootState) => state.tenantInfo.newTemplateLibraryFilters);
    const enforceStrictDocumentSetsOnly = useSelector((state: RootState) => state.tenantInfo.enforceStrictDocumentSetsOnly);

    const dispatch = useDispatch();

    const [menuItem_Upload, setMenuItem_Upload] = React.useState<IContextualMenuItem>();

    React.useEffect(() => {
        if (
            !selectedItems ||
            (selectedItems?.length === 0 && currentSharepointFolder?.userPermissions?.canAddListItem && currentSharepointFolder?.userPermissions?.canEditListItem && !searchValue && currentDirectory.type !== ComponentTypes.SharePointList)
        ) {
            var folderViewWrapper = new FolderViewWrapper(siteInfo, currentDirectory);
            const canViewFolder = folderViewWrapper.canViewFolder();

            const isInFolder = (currentDirectory?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.folder) && currentDirectory.serverRelativePath)
                || (currentDirectory?.directory?.length > 0 && currentDirectory?.parentId);

            let currentContentype: string;
            if (enforceStrictDocumentSetsOnly || enableNewTemplateFilters) {
                if (currentDirectory?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet)) {
                    currentContentype = SharePointFolderContentTypeIds.documentSet;
                } else if (isInFolder) {
                    currentContentype = SharePointFolderContentTypeIds.folder;
                } else {
                    currentContentype = rootContentType;
                }
            }

            let canUploadFolders = false;
            let canUploadFiles = false;

            if (enableNewTemplateFilters) {
                if (currentContentype === rootContentType && newTemplateLibraryFilters?.includes(newTemplateFilterEnum.file)) {
                    canUploadFiles = true;
                } else if (currentContentype === SharePointFolderContentTypeIds.folder && newTemplateFolderFilters?.includes(newTemplateFilterEnum.file)) {
                    canUploadFiles = true;
                } else if (currentContentype === SharePointFolderContentTypeIds.documentSet && newTemplateDocumentSetFilters?.includes(newTemplateFilterEnum.file)) {
                    canUploadFiles = true;
                }

                if (currentContentype === rootContentType && newTemplateLibraryFilters?.includes(newTemplateFilterEnum.folder)) {
                    canUploadFolders = true;
                } else if (currentContentype === SharePointFolderContentTypeIds.folder && newTemplateFolderFilters?.includes(newTemplateFilterEnum.folder)) {
                    canUploadFolders = true;
                } else if (currentContentype === SharePointFolderContentTypeIds.documentSet && newTemplateDocumentSetFilters?.includes(newTemplateFilterEnum.folder)) {
                    canUploadFolders = true;
                }
            } else {
                canUploadFiles = true;
                canUploadFolders = true;
            }

            if (enforceStrictDocumentSetsOnly
                && currentContentype === rootContentType
                && (canUploadFiles || canUploadFolders)
                && newDocumentTemplates?.some(template => template?.templateId?.startsWith(SharePointFolderContentTypeIds.documentSet))) {
                canUploadFiles = false;
                canUploadFolders = false;
            }

            if (canUploadFiles && (canViewFolder || (!canViewFolder && !isInFolder))) {
                const menuItems: IContextualMenuItem[] = [{
                    key: "uploadfiles",
                    text: languageStrings.Files,
                    onClick: () => {
                        dispatch(setIsSavingInBackground(false));
                        dispatch(setIsBusySaving({ isBusySaving: false, isSuccess: null, errorMessages: null }));
                        pickFiles();
                    }
                }];

                if (currentSharepointFolder.canCreateFolder && canUploadFolders) {
                    menuItems.push({
                        key: "uploadFolders",
                        text: languageStrings.Folder,
                        onClick: () => {
                            dispatch(setIsSavingInBackground(false));
                            dispatch(setIsBusySaving({ isBusySaving: false, isSuccess: null, errorMessages: null }));
                            pickFolder();
                        },
                    })
                }

                setMenuItem_Upload({
                    key: "uploadfilesfolders",
                    disabled: !currentDirectory?.sharePointListId || waitFor === true || isBusySaving === true,
                    text: languageStrings.Upload,
                    iconProps: { iconName: "Upload" },
                    subMenuProps: {
                        items: menuItems
                    }
                });
            } else {
                setMenuItem_Upload(null);
            }
        } else {
            setMenuItem_Upload(null);
        }
    }, [
        selectedItems,
        waitFor,
        newDocumentTemplates,
        currentSharepointFolder,
        isBusySaving,
        currentDirectory,
        siteInfo,
        searchValue,
        enableNewTemplateFilters,
        newTemplateLibraryFilters,
        newTemplateFolderFilters,
        newTemplateDocumentSetFilters,
    ]);

    return {
        menuItem_Upload,
    };
}
